import React, { useState, useEffect } from "react";
import config from "@config";
import apiEndpoints from "@store/apiEndpoints";
import errorFunctions from "@store/errorFunctions";
import { useToast } from "@components/Toast/ToastContext";
import SelectOption from "@components/Select/SelectOption";

const countryOptions = [
  { value: "AU", label: "Australia" },
  { value: "NZ", label: "New Zealand" },
];

const ServiceCenterTab = ({ brandId, protectedFetch }) => {
  const { showToast } = useToast();
  const [saving, setSaving] = useState(false);
  const [loadingExisting, setLoadingExisting] = useState(true);
  const [country, setCountry] = useState(
    countryOptions.find((c) => c.value === config.country) || countryOptions[0],
  );

  // Warranty >= 30 days
  const [gte30Central, setGte30Central] = useState(null);
  const [gte30Location, setGte30Location] = useState(null);
  const [gte30AssignedTo, setGte30AssignedTo] = useState(null);

  // Warranty < 30 days
  const [lt30Central, setLt30Central] = useState(null);
  const [lt30Location, setLt30Location] = useState(null);
  const [lt30AssignedTo, setLt30AssignedTo] = useState(null);

  useEffect(() => {
    protectedFetch(
      apiEndpoints.defaultServiceCenter.list(),
      "GET",
      null,
      errorFunctions.checkResponseThenJson,
    )
      .then((res) => {
        const items = Array.isArray(res) ? res : [];
        const gte30 = items.find((item) => item.min_days_since_purchase === 30);
        const lt30 = items.find((item) => item.max_days_since_purchase === 29);

        if (gte30) {
          setGte30Central(
            gte30.service_center_central
              ? {
                  value: gte30.service_center_central,
                  label:
                    gte30.service_center_central_name ||
                    `Central ${gte30.service_center_central}`,
                }
              : null,
          );
          setGte30Location(
            gte30.service_center
              ? {
                  value: gte30.service_center,
                  label:
                    gte30.service_center_name ||
                    `Service Center ${gte30.service_center}`,
                }
              : null,
          );
          setGte30AssignedTo(
            gte30.assigned_to
              ? {
                  value: gte30.assigned_to,
                  label: gte30.assigned_to_name || `User ${gte30.assigned_to}`,
                }
              : null,
          );
        }

        if (lt30) {
          setLt30Central(
            lt30.service_center_central
              ? {
                  value: lt30.service_center_central,
                  label:
                    lt30.service_center_central_name ||
                    `Central ${lt30.service_center_central}`,
                }
              : null,
          );
          setLt30Location(
            lt30.service_center
              ? {
                  value: lt30.service_center,
                  label:
                    lt30.service_center_name ||
                    `Service Center ${lt30.service_center}`,
                }
              : null,
          );
          setLt30AssignedTo(
            lt30.assigned_to
              ? {
                  value: lt30.assigned_to,
                  label: lt30.assigned_to_name || `User ${lt30.assigned_to}`,
                }
              : null,
          );
        }
      })
      .catch((err) => {
        // No existing settings — that's fine
        console.error("Failed to load existing service center settings", err);
      })
      .finally(() => {
        setLoadingExisting(false);
      });
  }, [brandId]);

  const buildPayload = (central, location, assignedTo, minDays, maxDays) => ({
    country: country.value,
    min_days_since_purchase: minDays,
    max_days_since_purchase: maxDays,
    brand: Number(brandId),
    service_center_central: central?.value,
    service_center: location?.value,
    assigned_to: assignedTo?.value || null,
  });

  const handleSave = () => {
    if (!country) {
      showToast("Please select a country", "error");
      return;
    }

    setSaving(true);
    const payloads = [];

    if (gte30Central && gte30Location) {
      payloads.push(
        buildPayload(
          gte30Central,
          gte30Location,
          gte30AssignedTo,
          30,
          2147483647,
        ),
      );
    }
    if (lt30Central && lt30Location) {
      payloads.push(
        buildPayload(lt30Central, lt30Location, lt30AssignedTo, 0, 29),
      );
    }

    if (payloads.length === 0) {
      showToast(
        "Please fill in at least one warranty service center (Central and Location required)",
        "warning",
      );
      setSaving(false);
      return;
    }

    Promise.all(
      payloads.map((payload) =>
        protectedFetch(
          apiEndpoints.defaultServiceCenter.create(),
          "POST",
          payload,
          errorFunctions.checkResponseThenJson,
        ),
      ),
    )
      .then(() => {
        showToast("Service center settings saved successfully", "success");
      })
      .catch(() => {
        showToast("Failed to save service center settings", "error");
      })
      .finally(() => {
        setSaving(false);
      });
  };

  const cardStyle = {
    flex: 1,
    padding: 20,
    borderRadius: 10,
    border: "1px solid #e5e7eb",
    background: "#fff",
  };

  const cardTitleStyle = {
    fontWeight: 600,
    fontSize: "0.9rem",
    color: "#1f2937",
    marginBottom: 16,
    paddingBottom: 10,
    borderBottom: "1px solid #e5e7eb",
  };

  const labelStyle = {
    display: "block",
    fontWeight: 500,
    fontSize: "0.85rem",
    color: "#374151",
    marginBottom: 6,
    marginTop: 12,
  };

  const centralEndpoint =
    config.backendServer + "service_center_central/dropdown/";
  const employeeEndpoint = config.backendServer + "employee/dropdown/";

  const locationEndpoint = (centralValue) =>
    centralValue
      ? config.backendServer +
        `service_center_central/${centralValue}/location/`
      : "";

  if (loadingExisting) {
    return (
      <div style={{ textAlign: "center", padding: 40 }}>
        <div className="spinner-border text-primary" role="status" />
      </div>
    );
  }

  return (
    <div>
      {/* Country */}
      <div style={{ maxWidth: 300, marginBottom: 16 }}>
        <label style={labelStyle}>Country</label>
        <select
          className="form-select"
          value={country?.value || ""}
          onChange={(e) => {
            const selected = countryOptions.find(
              (c) => c.value === e.target.value,
            );
            setCountry(selected || null);
          }}
          style={{
            width: "100%",
            padding: "8px 12px",
            borderRadius: 8,
            border: "1px solid #d1d5db",
            fontSize: "0.9rem",
          }}
          disabled={true}
        >
          {countryOptions.map((c) => (
            <option key={c.value} value={c.value}>
              {c.label}
            </option>
          ))}
        </select>
      </div>

      {/* Two cards side by side */}
      <div style={{ display: "flex", gap: 20, marginBottom: 20 }}>
        {/* Warranty >= 30 days */}
        <div style={cardStyle}>
          <div style={cardTitleStyle}>Warranty &gt;= 30 days</div>
          <div
            style={{ fontSize: "0.75rem", color: "#6b7280", marginBottom: 8 }}
          >
            min: 30 days &middot; max: unlimited
          </div>

          <label style={labelStyle}>Service Center Central</label>
          <SelectOption
            apiEndpoint={centralEndpoint}
            value={gte30Central}
            onChange={(val) => {
              setGte30Central(val);
              setGte30Location(null);
            }}
            placeholder="Select central..."
          />

          <label style={labelStyle}>Service Center / Location</label>
          <SelectOption
            key={gte30Central?.value || "no-central"}
            apiEndpoint={locationEndpoint(gte30Central?.value)}
            value={gte30Location}
            onChange={(val) => setGte30Location(val)}
            placeholder={
              gte30Central ? "Select location..." : "Select central first"
            }
            isDisabled={!gte30Central}
          />

          <label style={labelStyle}>Assigned To</label>
          <SelectOption
            apiEndpoint={employeeEndpoint}
            value={gte30AssignedTo}
            onChange={(val) => setGte30AssignedTo(val)}
            placeholder="Select employee..."
            isClearable
            valueKey="user_id"
          />
        </div>

        {/* Warranty < 30 days */}
        <div style={cardStyle}>
          <div style={cardTitleStyle}>Warranty &lt; 30 days</div>
          <div
            style={{ fontSize: "0.75rem", color: "#6b7280", marginBottom: 8 }}
          >
            min: 0 days &middot; max: 29 days
          </div>

          <label style={labelStyle}>Service Center Central</label>
          <SelectOption
            apiEndpoint={centralEndpoint}
            value={lt30Central}
            onChange={(val) => {
              setLt30Central(val);
              setLt30Location(null);
            }}
            placeholder="Select central..."
          />

          <label style={labelStyle}>Service Center / Location</label>
          <SelectOption
            key={lt30Central?.value || "no-central"}
            apiEndpoint={locationEndpoint(lt30Central?.value)}
            value={lt30Location}
            onChange={(val) => setLt30Location(val)}
            placeholder={
              lt30Central ? "Select location..." : "Select central first"
            }
            isDisabled={!lt30Central}
          />

          <label style={labelStyle}>Assigned To</label>
          <SelectOption
            apiEndpoint={employeeEndpoint}
            value={lt30AssignedTo}
            onChange={(val) => setLt30AssignedTo(val)}
            placeholder="Select employee..."
            isClearable
            valueKey="user_id"
          />
        </div>
      </div>

      {/* Save button */}
      <div style={{ display: "flex", justifyContent: "flex-end" }}>
        <button
          className="btn btn-primary btn-sm"
          onClick={handleSave}
          disabled={saving}
          style={{
            borderRadius: 8,
            padding: "6px 24px",
            fontWeight: 600,
          }}
        >
          {saving ? (
            <>
              <span
                className="spinner-border spinner-border-sm"
                role="status"
                style={{ marginRight: 6 }}
              />
              Saving...
            </>
          ) : (
            "Save"
          )}
        </button>
      </div>
    </div>
  );
};

export default ServiceCenterTab;
